import { motion } from 'framer-motion';

export interface MatchHistoryItem {
  id: string;
  roomName: string;
  roomCode: string;
  date: string;
  duration: string;
  playersCount: number;
  rank: number;
  prizeWon: string | null;
  status: string;
  isWin: boolean;
}

interface HistoryCardProps {
  match: MatchHistoryItem;
}

export function HistoryCard({ match }: HistoryCardProps) {
  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      style={{
        boxShadow: '0 4px 12px rgba(0,0,0,0.3), inset 0 0 12px rgba(255,255,255,0.01)',
      }}
      className="glass-light p-4 rounded-2xl border border-white/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-left select-none transition-colors hover:border-purple-500/10"
    >
      {/* 1. Profile information & title */}
      <div className="flex items-center space-x-3">
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center text-base shadow-inner border ${
            match.isWin
              ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
              : 'bg-slate-950/20 border-white/5 text-surface-500'
          }`}
        >
          {match.isWin ? '🏆' : '💀'}
        </div>
        <div className="flex flex-col text-left">
          <div className="flex items-center space-x-2">
            <h4 className="text-xs font-black text-white tracking-wide truncate max-w-[140px]">
              {match.roomName}
            </h4>
            <span className="px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/20 text-[8px] font-bold font-mono text-amber-400">
              {match.roomCode}
            </span>
          </div>
          <span className="text-[9px] font-mono text-surface-500 mt-0.5">
            {match.date} &bull; {match.duration}
          </span>
        </div>
      </div>

      {/* 2. Middle parameters: stats */}
      <div className="flex items-center space-x-6 text-[10px] font-mono">
        <div className="flex flex-col">
          <span className="text-[8px] text-surface-500 uppercase tracking-widest font-bold">
            Players
          </span>
          <span className="text-white font-black">{match.playersCount} Joined</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[8px] text-surface-500 uppercase tracking-widest font-bold">
            Rank
          </span>
          <span className="text-white font-black">#{match.rank}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-[8px] text-surface-500 uppercase tracking-widest font-bold">
            Prize Won
          </span>
          <span className={`font-black ${match.isWin ? 'text-purple-400' : 'text-surface-500'}`}>
            {match.prizeWon || 'None'}
          </span>
        </div>
      </div>

      {/* 3. Badge outcomes */}
      <div className="flex items-center space-x-2">
        <span className="px-2 py-0.5 rounded bg-slate-950/40 border border-white/5 text-[8px] font-black uppercase tracking-wider text-surface-500">
          {match.status}
        </span>
        <span
          className={`px-2.5 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest ${
            match.isWin
              ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20'
              : 'bg-red-500/15 text-red-400 border border-red-500/20'
          }`}
        >
          {match.isWin ? 'Win' : 'Loss'}
        </span>
      </div>
    </motion.div>
  );
}
export default HistoryCard;
